import React, { useEffect, useRef, useState } from 'react'
import { frameFor } from '../render/kit'
import { renderSlide, preparePost } from '../render/render'
import { imagesFor } from '../render/layouts'

// One slide of a post, drawn by the same renderer as the export. With
// onTapSlot the words and photos can be tapped; with onMovePhoto a photo can
// be dragged to reframe it.
// thumb: a small preview (smaller photos, nothing to tap)
const PostCanvas = ({ post, theme, format = post.format || 'post', slideIndex = 0, width = 360, onTapSlot, onMovePhoto, activeSlot, thumb = false, className = '' }) => {
  const canvasRef = useRef(null)
  const boxesRef = useRef([])
  const dragRef = useRef(null)
  const [images, setImages] = useState(null)
  const f = frameFor(format)
  const height = Math.round((width * f.H) / f.W)

  // Photos (and a seasonal design's picture) and fonts; a new one loads while
  // the old picture stays up
  const srcKey = JSON.stringify([imagesFor(post, theme), theme && theme.fontKey, theme && theme.logo, thumb])
  useEffect(() => {
    let alive = true
    preparePost(post, theme, { small: thumb }).then((imgs) => { if (alive) setImages(imgs) })
    return () => { alive = false }
  }, [srcKey]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const c = canvasRef.current
    if (!c || !images) return
    const dpr = Math.min(thumb ? 1.5 : 2, (typeof window !== 'undefined' && window.devicePixelRatio) || 1)
    boxesRef.current = renderSlide(c, { post, theme, images, format, slideIndex, scale: (width * dpr) / f.W })
  })

  const toUnits = (e) => {
    const r = canvasRef.current.getBoundingClientRect()
    return { x: ((e.clientX - r.left) / r.width) * f.W, y: ((e.clientY - r.top) / r.height) * f.H, k: f.W / r.width }
  }
  const hit = (pt) => {
    const list = boxesRef.current
    // Words sit over photos, so they win
    const words = list.filter(b => !b.photo && pt.x >= b.x - 12 && pt.x <= b.x + b.w + 12 && pt.y >= b.y - 12 && pt.y <= b.y + b.h + 12)
    if (words.length) return words[words.length - 1]
    const photos = list.filter(b => b.photo && pt.x >= b.x && pt.x <= b.x + b.w && pt.y >= b.y && pt.y <= b.y + b.h)
    return photos[photos.length - 1] || null
  }

  const onPointerDown = (e) => {
    if (!onTapSlot && !onMovePhoto) return
    const pt = toUnits(e)
    const box = hit(pt)
    if (!box) return
    dragRef.current = { box, start: pt, moved: false, key: box.slot.replace(/^photo:/, ''), photo: box.photo }
    if (box.photo && onMovePhoto) {
      const ph = (post.photos || {})[dragRef.current.key] || {}
      dragRef.current.fx = ph.fx == null ? 0.5 : ph.fx
      dragRef.current.fy = ph.fy == null ? 0.5 : ph.fy
      dragRef.current.zoom = Math.max(1, Number(ph.zoom) || 1)
      try { e.currentTarget.setPointerCapture(e.pointerId) } catch (x) { /* old browsers */ }
    }
  }
  const onPointerMove = (e) => {
    const d = dragRef.current
    if (!d) return
    const pt = toUnits(e)
    const dx = pt.x - d.start.x
    const dy = pt.y - d.start.y
    if (!d.moved && Math.hypot(dx, dy) < 10 * d.start.k) return
    d.moved = true
    if (!d.photo || !onMovePhoto) return
    e.preventDefault()
    // Dragging the photo right shows more of its left side
    const fx = Math.max(0, Math.min(1, d.fx - dx / (d.box.w * d.zoom * 1.4)))
    const fy = Math.max(0, Math.min(1, d.fy - dy / (d.box.h * d.zoom * 1.4)))
    onMovePhoto(d.key, { fx, fy })
  }
  const onPointerUp = () => {
    const d = dragRef.current
    dragRef.current = null
    if (d && !d.moved && onTapSlot) onTapSlot(d.box.slot)
  }

  const active = activeSlot && boxesRef.current.find(b => b.slot === activeSlot)
  const k = width / f.W

  return (
    <div className={`sx-canvas ${className}`} style={{ width, height }}>
      <canvas
        ref={canvasRef}
        style={{ width, height, touchAction: onMovePhoto ? 'none' : 'auto', cursor: onTapSlot ? 'pointer' : 'default' }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={() => { dragRef.current = null }}
        aria-label="Post preview"
        role="img"
      />
      {!images && <div className="sx-canvas-loading" aria-hidden />}
      {active && (
        <span className="sx-canvas-focus" style={{ left: active.x * k - 6, top: active.y * k - 6, width: active.w * k + 12, height: active.h * k + 12 }} aria-hidden />
      )}
    </div>
  )
}

export default PostCanvas
